import React from "react";
import { ColDef, ICellRendererParams, CellClickedEvent } from "ag-grid-community";
import VisibilityIcon from "@material-ui/icons/Visibility";
import get from "lodash.get";
import { QuickViewParams, QuickViewRenderer } from "../renderers/QuickViewRenderer";
import { ACTIONS_COL_ID, QUICK_VIEW_COL_ID, CorreColDef } from "../colDef";
import { CustomAction, IActionable } from "../../../models/actions";
import { ActionsCellRenderer } from "../renderers/ActionsCellRenderer";
import { UIActions } from "../../peer-support/Actions";
import { IFormField } from "../../../laco-common";
import CustomTooltip, { CustomTooltipDisplayMode } from "../CustomTooltip";
import { IImage } from "../../../models/Image";
import ImageView from "../../ui/Media/ImageView";
import { FileType } from "../../../laco-common/base/models/IFile";

export function getQuickViewColDef(
	cols: ColDef[],
	getId?: (data: any) => string,
	getTitle?: (data: any) => string,
	getUrl?: (data: any) => string,
	quickviewParams?: QuickViewParams
): ColDef {
	return {
		field: QUICK_VIEW_COL_ID,
		colId: QUICK_VIEW_COL_ID,
		pinned: "left",
		headerTooltip: "View",
		headerName: "View",
		headerValueGetter: (params: { colDef: ColDef; location: "header" }) => {
			if (params.location === "header") return "";
			return params.colDef.headerName;
		},
		width: 50,
		minWidth: 50,
		maxWidth: 50,
		filter: false,
		sortable: false,
		// enableRowGroup: false, // enterprise only
		flex: 0,
		cellClass: "quick-view px-0 text-center",
		cellRendererFramework: (params: ICellRendererParams) => {
			if (params.node && params.node.group) return <span></span>;
			return (
				<QuickViewRenderer
					{...params}
					colDefs={cols}
					getId={getId}
					getTitle={getTitle}
					getUrl={getUrl}
					{...quickviewParams}
				/>
			);
		},
	};
}

export function getActionsColumnDef1<T>(
	component: IActionable<T>,
	predicate?: (data) => boolean,
	customActions?: CustomAction[],
	width?: number
): ColDef {
	return {
		headerName: "Actions",
		headerTooltip: "Actions",
		field: undefined,
		colId: ACTIONS_COL_ID,
		pinned: "right",
		// displayInListView: true, // TODO
		// listViewCellClass: "align-self-end pl-1 mr-minus-10px d-inline-block",
		cellRendererFramework: (params: ICellRendererParams) => {
			if (params.node && params.node.group) return "";
			return !predicate || predicate(params.data) ? (
				<ActionsCellRenderer {...params} component={component} customActions={customActions} />
			) : (
				<span />
			);
		},
		width: width || 100,
		maxWidth: width || 100,
		suppressSizeToFit: true,
		enableRowGroup: false,
		rowGroup: false,
		suppressColumnsToolPanel: false,
		sortable: false,
		filter: false,
		suppressMenu: false,
		// suppressContextMenu: true
	};
}

export function getActionsColumnDef<T>(
	component: IActionable<T>,
	getTooltipField?: (data) => string,
	predicate?: (data) => boolean,
	colDefs?: ColDef[],
	customActions?: CustomAction[],
	getCustomActions?: (data) => CustomAction[],
	width?: number
): ColDef {
	let actionsCount: number = 1 + (customActions?.length || 0);
	if (component.onDelete) actionsCount++;
	const editProps = component.onEdit ? { mode: "Edit", entity: "member", onSubmit: () => Promise.resolve() } : null; // sheri todo
	if (editProps) actionsCount++;
	const colWidth: number = Math.max(width || actionsCount * 55, 65);

	return {
		headerName: "Actions",
		headerTooltip: "Actions",
		field: undefined,
		colId: ACTIONS_COL_ID,
		pinned: "right",
		// displayInListView: true, // TODO
		// listViewCellClass: "align-self-end pl-1 mr-minus-10px d-inline-block",
		cellRendererFramework: (params: ICellRendererParams) => {
			if (params.node && params.node.group) return "";
			const data = params.data;
			const itemName: string = getTooltipField && data ? getTooltipField(data) || `#${data?.id}` : `#${data?.id}`;
			const disableDelete: boolean = (component.disableDelete && component.disableDelete(data)) || data.noDelete;
			const disabledTitle: string = disableDelete
				? component.getDisabledTitle
					? component.getDisabledTitle(data)
					: `Can't delete ${itemName}`
				: `Delete ${itemName}`;
			let itemCustomActions: CustomAction[] = [];
			if (getCustomActions) {
				itemCustomActions = getCustomActions(data) || [];
			}

			return !predicate || predicate(data) ? (
				// <ActionsCellRenderer {...params} component={component} customActions={customActions} />
				<UIActions
					data={data}
					itemName={itemName}
					onEdit={component.onEdit}
					onDelete={component.onDelete}
					disableDelete={disableDelete}
					disabledTitle={disabledTitle}
					// editEntityProps={
					// 	component.onEdit
					// 		? { mode: "Edit", entity: "member", onSubmit: () => Promise.resolve() }
					// 		: undefined
					// }
					colDefs={colDefs}
					customActions={[
						...itemCustomActions,
						...(customActions || []).map((action: CustomAction) => {
							return action;
						}),
						{
							getTitle: (data) => `View ${itemName}`,
							callback: (data) => {
								return component.onPreview(data, params);
							},
							icon: VisibilityIcon,
						},
					]}
				/>
			) : (
				<span />
			);
		},
		width: colWidth,
		// maxWidth: colWidth,
		// minWidth: colWidth,
		cellClass: "d-flex w-100 justify-content-end px-0",
		resizable: false,
		suppressSizeToFit: true,
		suppressAutoSize: true,
		enableRowGroup: false,
		rowGroup: false,
		suppressColumnsToolPanel: false,
		sortable: false,
		filter: false,
		suppressMenu: true,
		// suppressContextMenu: true
	};
}

export const getCustomTooltipDrillColDef = (
	col: CorreColDef,
	field: IFormField,
	findDrillItem: (item, props) => boolean
): CorreColDef => {
	const tooltipDisplayMode: CustomTooltipDisplayMode = CustomTooltipDisplayMode.DrillToCollection;

	return {
		cellClass: "gray-link underline",
		tooltipComponent: "customTooltip",
		tooltipField: field.id,
		tooltipComponentParams: {
			field,
			useFirewire: (field as any).useFirewire,
			tooltipDisplayMode,
			findDrillItem,
		},
		listViewOmitCellClass: true,
		listViewRenderer: (params: any) => {
			if (!params.api) {
				return get(params.data, field.id);
			}
			return (
				<CustomTooltip
					field={field}
					{...params}
					className="static"
					showDrillValue={true}
					tooltipDisplayMode={tooltipDisplayMode}
					useFirewire={(field as any).useFirewire}
				/>
			);
		},
		...col,
	};
};

export const getCustomTooltipImageColDef = (col: CorreColDef, field: IFormField): CorreColDef => {
	const tooltipDisplayMode: CustomTooltipDisplayMode = CustomTooltipDisplayMode.Image;

	return {
		cellClass: "gray-link underline",
		tooltipComponent: "customTooltip",
		tooltipField: field.id,
		tooltipComponentParams: {
			field,
			useFirewire: (field as any).useFirewire,
			tooltipDisplayMode,
		},
		cellRendererFramework: (params: ICellRendererParams) => {
			const imageUrl: string = params.value;
			if (!imageUrl) return null;
			const image: IImage = { fileType: FileType.IMAGE, url: imageUrl, filename: imageUrl };
			return (
				<div className="link" onClick={(e) => window.open(imageUrl, "_blank")}>
					<div className="float-left mr-2" style={{ marginTop: "2px" }}>
						<ImageView image={image} viewWidth={50} viewHeight={42} />
					</div>
					<a
						className="gray-link text-xs"
						href={imageUrl}
						target="_blank"
						onClick={(e) => e.stopPropagation()}
					>
						{imageUrl}
					</a>
				</div>
			);
		},
		listViewOmitCellClass: true,
		listViewRenderer: (params: any) => {
			const imageUrl: string = params.value;
			if (!imageUrl) return null;
			const image: IImage = { fileType: FileType.IMAGE, url: imageUrl, filename: imageUrl };
			return (
				<div className="d-flex flex-column flex-md-row link" onClick={(e) => window.open(imageUrl, "_blank")}>
					<div className="" style={{ marginTop: "2px" }}>
						<ImageView image={image} viewWidth={250} viewHeight={200} />
					</div>
					<a
						className="gray-link mt-2-mobile ml-3-desktop text-xs align-self-end pr-2-mobile"
						href={imageUrl}
						target="_blank"
						onClick={(e) => e.stopPropagation()}
					>
						{imageUrl}
					</a>
				</div>
			);
		},
		...col,
	};
};

export const getCustomTooltipContentColDef = (
	col: CorreColDef,
	field: IFormField,
	cellRendererFramework: (params: ICellRendererParams) => React.ReactNode,
	listViewRenderer?: (params) => React.ReactNode,
	renderContent?: (params) => React.ReactNode
): CorreColDef => {
	const tooltipDisplayMode: CustomTooltipDisplayMode = CustomTooltipDisplayMode.Content;
	return {
		cellClass: "gray-link underline",
		tooltipComponent: "customTooltip",
		tooltipField: field.id,
		tooltipComponentParams: {
			field,
			useFirewire: (field as any).useFirewire,
			tooltipDisplayMode,
			renderContent: renderContent || cellRendererFramework,
		},
		cellRendererFramework,
		listViewOmitCellClass: true,
		listViewRenderer,
		...col,
	};
};
